import { useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaPlus, FaTimes } from "react-icons/fa";
import {
  MAX_MEASURES,
  MAX_SUMMARY_GROUPS,
  addMeasure,
  groupOptions,
  measureOptions,
  removeMeasure,
  setSummaryGroup,
  setSummarySort,
  type PathOption,
} from "../definition";
import { bucketName, groupLabel, labelOf, measureLabel } from "../labels";
import type { Bucket, Catalog, MeasureKind, ReportSpec, SortDirection } from "../types";
import { ghostButton, input } from "./styles";

type Props = { catalog: Catalog; definition: ReportSpec; onChange: (definition: ReportSpec) => void };

const BUCKETS: Bucket[] = ["YEAR", "SCHOOL_YEAR", "MONTH"];
const KINDS: MeasureKind[] = ["SUM", "AVG", "MIN", "MAX"];

function OptionSelect({ options, value, onChange, label }: {
  options: PathOption[]; value: string; onChange: (path: string) => void; label: string;
}): ReactElement {
  const { t } = useTranslation();
  const groups = Array.from(new Set(options.map((option) => option.group)));
  return (
    <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value)} className={input}>
      <option value="">{t("reports.builder.choose", { defaultValue: "Choose…" })}</option>
      {groups.map((group) => (
        <optgroup key={group} label={group}>
          {options.filter((option) => option.group === group).map((option) => (
            <option key={option.path} value={option.path}>{option.label}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

/** Group by (at most two values), calculations, and the order of the groups. */
export default function SummaryEditor({ catalog, definition, onChange }: Props): ReactElement {
  const { t } = useTranslation();
  const summary = definition.summary ?? { groups: [], measures: [{ kind: "COUNT" as const }], sort: [] };
  const label = (item: { labelKey: string; defaultLabel: string }) => labelOf(t, item);
  const countLabel = (name: string) => String(t("reports.builder.count_of", { defaultValue: "{{name}}: number", name }));
  const groupChoices = useMemo(() => groupOptions(catalog, definition.source, label, countLabel),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalog, definition.source, t]);
  const numberChoices = useMemo(() => measureOptions(catalog, definition.source, label, countLabel),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalog, definition.source, t]);
  const [kind, setKind] = useState<MeasureKind>("AVG");
  const [measurePath, setMeasurePath] = useState("");

  const sortKey = summary.sort[0]?.key ?? "";
  const sortDirection: SortDirection = summary.sort[0]?.direction ?? "DESC";
  const heading = "text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400";

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <h3 className={heading}>{t("reports.builder.group_by", { defaultValue: "Group by" })}</h3>
        {[...summary.groups, null].slice(0, MAX_SUMMARY_GROUPS).map((group, index) => {
          const option = groupChoices.find((item) => item.path === group?.path);
          return (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className="space-y-1">
              <div className="flex items-center gap-1">
                <OptionSelect
                  label={String(t("reports.builder.group_by", { defaultValue: "Group by" }))}
                  options={groupChoices}
                  value={group?.path ?? ""}
                  onChange={(path) => {
                    const chosen = groupChoices.find((item) => item.path === path);
                    onChange(setSummaryGroup(definition, index,
                      path ? { path, bucket: chosen?.isDate ? "YEAR" : null } : null));
                  }}
                />
                {group && (
                  <button type="button" className={ghostButton}
                          aria-label={String(t("reports.builder.remove", { defaultValue: "Remove" }))}
                          onClick={() => onChange(setSummaryGroup(definition, index, null))}>
                    <FaTimes aria-hidden />
                  </button>
                )}
              </div>
              {group && option?.isDate && (
                <select
                  aria-label={String(t("reports.builder.period", { defaultValue: "Period" }))}
                  value={group.bucket ?? ""}
                  onChange={(event) => onChange(setSummaryGroup(definition, index,
                    { path: group.path, bucket: (event.target.value || null) as Bucket | null }))}
                  className={input}
                >
                  {BUCKETS.map((bucket) => <option key={bucket} value={bucket}>{bucketName(t, bucket)}</option>)}
                  <option value="">{t("reports.builder.per_day", { defaultValue: "per day" })}</option>
                </select>
              )}
            </div>
          );
        })}
      </section>

      <section className="space-y-2">
        <h3 className={heading}>{t("reports.builder.calculations", { defaultValue: "Calculations" })}</h3>
        <ul className="space-y-1">
          {summary.measures.map((measure, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={index} className="flex items-center justify-between gap-2 rounded-xl bg-slate-50 px-3 py-1.5 text-sm text-slate-700 dark:bg-white/5 dark:text-slate-200">
              <span className="min-w-0 truncate">{measureLabel(t, catalog, definition.source, measure)}</span>
              {summary.measures.length > 1 && (
                <button type="button" className={ghostButton}
                        aria-label={String(t("reports.builder.remove", { defaultValue: "Remove" }))}
                        onClick={() => onChange(removeMeasure(definition, index))}>
                  <FaTimes aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
        {summary.measures.length < MAX_MEASURES && (
          <div className="space-y-1 rounded-2xl border border-dashed border-slate-300 p-2 dark:border-white/15">
            <select aria-label={String(t("reports.builder.calculation", { defaultValue: "Calculation" }))}
                    value={kind} onChange={(event) => setKind(event.target.value as MeasureKind)}
                    className={input}>
              {KINDS.map((item) => (
                <option key={item} value={item}>
                  {t(`reports.builder.measure_kinds.${item}`, {
                    defaultValue: ({ SUM: "Total", AVG: "Average", MIN: "Lowest", MAX: "Highest" } as Record<string, string>)[item],
                  })}
                </option>
              ))}
              {!summary.measures.some((measure) => measure.kind === "COUNT") && (
                <option value="COUNT">{t("reports.builder.measure_kinds.COUNT", { defaultValue: "Number of rows" })}</option>
              )}
            </select>
            {kind !== "COUNT" && (
              <OptionSelect label={String(t("reports.builder.of", { defaultValue: "Of" }))} options={numberChoices}
                            value={measurePath} onChange={setMeasurePath} />
            )}
            <button
              type="button"
              className={ghostButton}
              disabled={kind !== "COUNT" && !measurePath}
              onClick={() => {
                onChange(addMeasure(definition, kind === "COUNT" ? { kind: "COUNT" } : { kind, path: measurePath }));
                setMeasurePath("");
              }}
            >
              <FaPlus aria-hidden /> {t("reports.builder.add_calculation", { defaultValue: "Add calculation" })}
            </button>
            {numberChoices.length === 0 && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {t("reports.builder.no_numbers", { defaultValue: "This subject has no numbers to calculate with." })}
              </p>
            )}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h3 className={heading}>{t("reports.builder.order", { defaultValue: "Order" })}</h3>
        <select
          aria-label={String(t("reports.builder.order", { defaultValue: "Order" }))}
          value={sortKey}
          onChange={(event) => onChange(setSummarySort(definition, event.target.value || null, sortDirection))}
          className={input}
        >
          <option value="">{t("reports.builder.order_by_groups", { defaultValue: "By group" })}</option>
          {summary.groups.map((group, index) => (
            <option key={`g${index}`} value={`g${index}`}>{groupLabel(t, catalog, definition.source, group)}</option>
          ))}
          {summary.measures.map((measure, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <option key={`m${index}`} value={`m${index}`}>{measureLabel(t, catalog, definition.source, measure)}</option>
          ))}
        </select>
        {sortKey && (
          <select
            aria-label={String(t("reports.builder.direction", { defaultValue: "Direction" }))}
            value={sortDirection}
            onChange={(event) => onChange(setSummarySort(definition, sortKey, event.target.value as SortDirection))}
            className={input}
          >
            <option value="DESC">{t("reports.builder.desc", { defaultValue: "Highest first" })}</option>
            <option value="ASC">{t("reports.builder.asc", { defaultValue: "Lowest first" })}</option>
          </select>
        )}
      </section>
    </div>
  );
}
